//
// Copyright 2026 DXOS.org
//

import * as Effect from 'effect/Effect';
import * as Schema from 'effect/Schema';
import * as Migrator from 'effect/sql/Migrator';
import * as SqlClient from 'effect/sql/SqlClient';
import type * as SqlError from 'effect/sql/SqlError';
import type * as Statement from 'effect/sql/Statement';

import {
  ATTR_DELETED,
  ATTR_META,
  ATTR_PARENT,
  ATTR_RELATION_SOURCE,
  ATTR_RELATION_TARGET,
  ATTR_TYPE,
} from '@dxos/echo/internal';
import { invariant } from '@dxos/invariant';
import { DXN, EID, EntityId, SpaceId, URI } from '@dxos/keys';

import { localEntityId } from '../entity-ids.ts';
import { MIGRATIONS, MIGRATIONS_TABLE } from '../migrations/entity-meta/index.ts';
import {
  MAX_CHUNKED_STATEMENTS,
  SqlBoundVariableLimit,
  chunkArray,
  chunkSizeForBoundVariables,
  countBoundVariables,
  measuredVariableCost,
  mergeChunkedRows,
  planChunkPairs,
  planChunks,
  readConsistently,
} from '../utils.ts';
import type { IndexerObject } from './interface.ts';
import type { Index } from './interface.ts';

/**
 * Normalizes an echo: EID to the local (unqualified) form so SQL comparisons are consistent.
 * Rows are indexed in the canonical `echo:///<entityId>` form; a space-qualified
 * `echo://<space>/<entityId>` would otherwise miss every row for that type.
 */
const _normalizeTypeUri = (typeDXN: string): string => {
  if (!typeDXN.startsWith('echo:')) {
    return typeDXN;
  }
  const eid = EID.tryParse(typeDXN);
  if (!eid) {
    return typeDXN;
  }
  const entityId = EID.getEntityId(eid);
  return entityId ? EID.make({ entityId }) : typeDXN;
};

/**
 * Every stored `typeDXN` form equivalent to a normalized type identifier. Rows written before
 * `echo:///<id>` became the canonical local EID hold the single-slash `echo:/<id>` form; both
 * address the same stored schema, so queries must match either without requiring a reindex.
 */
const _typeUriEquivalents = (normalized: string): string[] => {
  if (normalized.startsWith('echo:///')) {
    return [normalized, `echo:/${normalized.slice('echo:///'.length)}`];
  }
  return [normalized];
};

const _escapeLikePrefix = (prefix: string) => {
  // Escape LIKE metacharacters in the *literal* prefix (we still append a wildcard for the version suffix).
  // Backslash is used as the ESCAPE character.
  // See: https://www.sqlite.org/lang_expr.html#like
  const escaped = prefix.replaceAll('\\', '\\\\').replaceAll('%', '\\%').replaceAll('_', '\\_');
  return `${escaped}:%`;
};

/** The stored forms a type identifier matches, and whether it also matches versioned rows by prefix. */
const _typeDxnMatch = (typeDXN: string): { forms: string[]; hasNoVersion: boolean } => {
  const normalized = _normalizeTypeUri(typeDXN);
  const parsedDxn = DXN.isDXN(normalized) ? normalized : undefined;
  return {
    forms: _typeUriEquivalents(normalized),
    hasNoVersion: parsedDxn !== undefined && DXN.getVersion(parsedDxn) === undefined,
  };
};

/**
 * WHERE fragment matching the `typeDXN` column against any of the given type identifiers,
 * covering legacy-equivalent stored forms and — for versionless DXNs — versioned rows via a
 * LIKE prefix. Shared with `FtsIndex` so type scoping matches identically across indexes.
 */
export const buildTypeDxnCondition = (sql: SqlClient.SqlClient, typeDxns: readonly string[]): Statement.Fragment =>
  sql.or(
    typeDxns.map((typeDXN) => {
      const { forms, hasNoVersion } = _typeDxnMatch(typeDXN);
      const exactMatch = sql.or(forms.map((form) => sql`typeDXN = ${form}`));
      if (!hasNoVersion) {
        return exactMatch;
      }
      // A range bounds the seek on `(spaceId, typeDXN)`; `= OR LIKE` alone makes SQLite scan the
      // space partition. `;` is the code point after `:`, so the range covers the bare form and
      // every `form:<version>`; the exact predicate stays as the residual because the range also
      // admits `form` followed by a code point below `:`.
      return sql.or(
        forms.map(
          (form) =>
            sql`(typeDXN >= ${form} AND typeDXN < ${form + ';'} AND (typeDXN = ${form} OR typeDXN LIKE ${_escapeLikePrefix(form)} ESCAPE '\\'))`,
        ),
      );
    }),
  );

export const EntityMeta = Schema.Struct({
  recordId: Schema.Number,
  objectId: EntityId,
  /** Empty string for non-queue objects. */
  queueId: Schema.String,
  /** Queue subspace namespace (e.g. 'data', 'trace'). Empty string for non-queue objects. */
  queueNamespace: Schema.String,
  spaceId: SpaceId,
  documentId: Schema.String,
  entityKind: Schema.String,
  /**
   * Type identifier URI for the object — typename DXN for non-stored schemas,
   * schema-as-object EID for stored (dynamic) schemas. Mirrors the value
   * written into the object's `system.type`.
   */
  typeDXN: URI.Schema,
  deleted: Schema.Boolean,
  source: Schema.NullOr(EID.Schema),
  target: Schema.NullOr(EID.Schema),
  /** Parent object id (nullable). */
  parent: Schema.NullOr(EID.Schema),
  /** Caller-supplied domain identity from `meta.convergenceKey` (nullable); duplicates sharing one merge. */
  convergenceKey: Schema.NullOr(Schema.String),
  /** JSON of `meta.annotations`, or null when the entity carries none. */
  annotations: Schema.NullOr(Schema.String),
  /** Monotonically increasing sequence number assigned on insert/update for tracking indexing order. */
  version: Schema.Number,
  /** Unix ms timestamp when the object was first indexed. */
  createdAt: Schema.NullOr(Schema.Number),
  /** Unix ms timestamp when the object was last re-indexed. */
  updatedAt: Schema.NullOr(Schema.Number),
  /**
   * Global position assigned to the object's feed block — the insertion id a feed cursor names.
   * Null for automerge objects and for feed blocks not yet positioned.
   */
  queuePosition: Schema.NullOr(Schema.Number),
});
export interface EntityMeta extends Schema.Schema.Type<typeof EntityMeta> {}

/**
 * A queue to select from, scoped by the space that owns it.
 *
 * `spaceId` is absent only for an unqualified feed URI (`echo:///<id>`), which names no space to
 * scope to; such a queue matches on its id alone, as every queue read did before scoping existed.
 */
export interface QueueRef {
  readonly queueId: string;
  readonly spaceId?: string;
}

/** One source a read selects from: a whole space, or one queue. */
export type SourceRef = { readonly spaceId: string } | { readonly queue: QueueRef };

/** The sources of a read, as the unit its source condition is chunked by. */
export const sourceRefs = (
  spaceIds: readonly string[],
  queues: readonly QueueRef[] | null | undefined,
): SourceRef[] => [...spaceIds.map((spaceId) => ({ spaceId })), ...(queues ?? []).map((queue) => ({ queue }))];

/** Splits sources back into the space ids and queues a source condition is built from. */
export const splitSourceRefs = (sources: readonly SourceRef[]): { spaceIds: string[]; queues: QueueRef[] } => ({
  spaceIds: sources.flatMap((source) => ('spaceId' in source ? [source.spaceId] : [])),
  queues: sources.flatMap((source) => ('queue' in source ? [source.queue] : [])),
});

/**
 * Builds a SQL condition for filtering by space and queue source.
 * When `includeAllQueues` is false and no `queues`, only non-queue objects are returned.
 */
export const buildSourceCondition = (
  sql: SqlClient.SqlClient,
  spaceIds: readonly string[],
  includeAllQueues: boolean,
  queues: readonly QueueRef[] | null,
): Statement.Fragment => buildSourceRefsCondition(sql, sourceRefs(spaceIds, queues), includeAllQueues);

/** {@link buildSourceCondition} over one chunk of a read's sources. */
const buildSourceRefsCondition = (
  sql: SqlClient.SqlClient,
  sources: readonly SourceRef[],
  includeAllQueues: boolean,
): Statement.Fragment => {
  const { spaceIds, queues } = splitSourceRefs(sources);
  const conditions: Statement.Fragment[] = [];

  if (spaceIds.length > 0) {
    if (includeAllQueues) {
      conditions.push(sql`${sql.in('spaceId', spaceIds)}`);
    } else {
      conditions.push(sql`(${sql.in('spaceId', spaceIds)} AND queueId = '')`);
    }
  }

  if (queues.length > 0) {
    // Each queue carries its own space: a queue id is unique only within one, so matching on the id
    // alone would admit another space's rows for a colliding id.
    conditions.push(
      sql.or(
        queues.map((queue) =>
          queue.spaceId !== undefined
            ? sql`(spaceId = ${queue.spaceId} AND queueId = ${queue.queueId})`
            : sql`queueId = ${queue.queueId}`,
        ),
      ),
    );
  }

  if (conditions.length === 0) {
    return sql`1 = 0`;
  }

  return sql.or(conditions);
};

/**
 * Window over a queue-scoped read: which rows, in what order, and how many.
 *
 * Only meaningful for a queue-scoped query — `queuePosition` is null for automerge objects and the
 * caller-visible orderings differ, so a caller must not apply this to a query that also selects
 * from a space's documents.
 */
export type QueueWindow = CursorQueueWindow | NaturalQueueWindow;

/**
 * Resume a feed after a cursor position: positioned blocks only, in position order.
 */
export interface CursorQueueWindow {
  readonly kind: 'cursor';
  /** Exclusive lower bound: only blocks positioned strictly after this are returned. */
  readonly after: number;
  /** Exclusive upper bound: only blocks positioned strictly before this are returned. */
  readonly before?: number;
  /** Maximum rows to return, applied after ordering. */
  readonly limit?: number;
}

/**
 * Read the first `limit` rows of a feed in natural order, so a bounded query costs what it asks
 * for rather than the whole feed. Ordered by `objectId` because that is the key the query
 * executor's natural comparator uses: the capped page is then exactly the page a full scan
 * followed by an in-memory sort would have produced.
 */
export interface NaturalQueueWindow {
  readonly kind: 'natural';
  readonly direction: 'asc' | 'desc';
  /** Maximum rows to return, applied after ordering. */
  readonly limit: number;
  /**
   * Restrict to rows with this deleted state. Folded in here rather than left to the caller so the
   * cap counts only rows the caller keeps — a limit applied before the deleted filter would return
   * short of what the caller asked for.
   */
  readonly deleted?: boolean;
}

/**
 * Trailing `... AND <bounds> ORDER BY <key> LIMIT ?` for a windowed queue read.
 * Empty when there is no window, so the unwindowed query keeps its previous shape (and its
 * unspecified row order).
 */
export const buildQueueWindow = (sql: SqlClient.SqlClient, window: QueueWindow | undefined): Statement.Fragment => {
  if (window === undefined) {
    return sql``;
  }

  // `recordId` breaks ties in every ordering: a read split across statements is merged in memory,
  // and each statement's LIMIT must cut at the same rows the merge will.
  if (window.kind === 'natural') {
    const deleted = window.deleted !== undefined ? sql` AND deleted = ${window.deleted ? 1 : 0}` : sql``;
    // Unpositioned blocks are in scope here, unlike a cursor read: a natural read is over the feed
    // as the caller sees it, and a locally appended block is part of that before it is positioned.
    const order =
      window.direction === 'desc'
        ? sql` ORDER BY objectId DESC, recordId DESC`
        : sql` ORDER BY objectId ASC, recordId ASC`;
    return sql`${deleted}${order} LIMIT ${window.limit}`;
  }

  // A cursor read is over positioned blocks only — `queuePosition > ?` excludes the nulls, and
  // that is the intent: an unpositioned block has no place in the ordering yet, so admitting it
  // would let it slip past a later read that resumes beyond its eventual position.
  const upper = window.before !== undefined ? sql` AND queuePosition < ${window.before}` : sql``;
  const limit = window.limit !== undefined ? sql` LIMIT ${window.limit}` : sql``;
  return sql` AND queuePosition > ${window.after}${upper} ORDER BY queuePosition ASC, recordId ASC${limit}`;
};

/** Code-unit order, as SQLite's default BINARY collation sorts the ASCII ids compared here. */
const compareText = (left: string, right: string): number => (left < right ? -1 : left > right ? 1 : 0);

/** The order and cut {@link buildQueueWindow} applies, for merging a read issued as several statements. */
const queueWindowMerge = (
  window: QueueWindow | undefined,
): { compare?: (left: EntityMeta, right: EntityMeta) => number; limit?: number } => {
  if (window === undefined) {
    return {};
  }
  if (window.kind === 'natural') {
    const direction = window.direction === 'desc' ? -1 : 1;
    return {
      compare: (left, right) =>
        direction * (compareText(left.objectId, right.objectId) || left.recordId - right.recordId),
      limit: window.limit,
    };
  }
  return {
    // A cursor read selects positioned rows only, so neither position is null here.
    compare: (left, right) => (left.queuePosition ?? 0) - (right.queuePosition ?? 0) || left.recordId - right.recordId,
    limit: window.limit,
  };
};

/** Widens a window's limit by `extra` rows; an unlimited window stays unlimited. */
const widenQueueWindow = (window: QueueWindow | undefined, extra: number): QueueWindow | undefined =>
  window?.limit === undefined ? window : { ...window, limit: window.limit + extra };

/** SQLite stores booleans as integers. */
const withDeletedFlag = (row: EntityMeta): EntityMeta => ({ ...row, deleted: !!row.deleted });

export class EntityMetaIndex implements Index {
  readonly #sql: SqlClient.SqlClient;

  constructor(sql: SqlClient.SqlClient) {
    this.#sql = sql;
  }

  /**
   * Applies any migrations this database has not recorded yet.
   */
  migrate = Effect.fn('EntityMetaIndex.runMigrations')(() =>
    Migrator.make({})({ loader: Migrator.fromRecord(MIGRATIONS), table: MIGRATIONS_TABLE }).pipe(
      // A malformed bundled manifest is a defect, not something a caller can recover from.
      Effect.catchTag('MigrationError', (error) => Effect.die(error)),
      Effect.asVoid,
      Effect.provideService(SqlClient.SqlClient, this.#sql),
    ),
  );

  /**
   * Document-backed object rows carrying any of the given convergence keys in one space.
   *
   * The detection point-lookup for convergence-key merging: called with only the keys seen in a just
   * indexed batch, so its cost is proportional to writes that carry a convergence key. Tombstoned
   * rows are included — a merged-away loser that received late edits must be found so those
   * edits can be folded into the winner; the merge re-verifies every row against its document.
   */
  queryByConvergenceKeys = Effect.fn('EntityMetaIndex.queryByConvergenceKeys')(
    (spaceId: SpaceId, convergenceKeys: readonly string[]): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        if (convergenceKeys.length === 0) {
          return [];
        }
        const sql = this.#sql;
        // Chunked against the bound-variable budget — an initial index of a fresh clone can
        // present thousands of keys in one batch, and a thrown query here would skip convergence
        // detection for the whole batch with no retry. Distinct keys so a repeated one cannot
        // straddle two chunks and duplicate its row.
        const results: EntityMeta[] = [];
        for (const chunk of chunkArray([...new Set(convergenceKeys)])) {
          const rows =
            yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE spaceId = ${spaceId} AND ${sql.in('convergenceKey', chunk)} AND entityKind = 'object' AND queueId = ''`;
          results.push(...rows.map((row) => ({ ...row, deleted: !!row.deleted })));
        }
        return results;
      }),
  );

  query = Effect.fn('EntityMetaIndex.query')(
    (query: Pick<EntityMeta, 'spaceId' | 'typeDXN'>): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        // SQLite stores booleans as integers, so we need to specify the raw row type.
        const rows =
          yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE spaceId = ${query.spaceId} AND (${buildTypeDxnCondition(sql, [query.typeDXN])})`;
        return rows.map((row) => ({
          ...row,
          deleted: !!row.deleted,
        }));
      }),
  );

  queryAll = Effect.fn('EntityMetaIndex.queryAll')(
    (query: {
      spaceIds: readonly EntityMeta['spaceId'][];
      includeAllQueues?: boolean;
      queues?: readonly QueueRef[] | null;
      window?: QueueWindow;
    }): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sources = sourceRefs(query.spaceIds, query.queues);
        if (sources.length === 0) {
          return [];
        }

        const sql = this.#sql;
        const includeAllQueues = query.includeAllQueues ?? false;
        const window = buildQueueWindow(sql, query.window);
        const budget = (yield* SqlBoundVariableLimit) - countBoundVariables(sql, window);
        const sourceCost = measuredVariableCost(sql, (chunk: readonly SourceRef[]) =>
          buildSourceRefsCondition(sql, chunk, includeAllQueues),
        );
        const chunks = planChunks(sources, sourceCost, budget);
        const results = yield* readConsistently(
          sql,
          chunks.length,
          Effect.forEach(
            chunks,
            (chunk) =>
              sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${buildSourceRefsCondition(sql, chunk, includeAllQueues)}${window}`,
          ),
        );
        return mergeChunkedRows(results, queueWindowMerge(query.window)).map(withDeletedFlag);
      }),
  );

  queryTypes = Effect.fn('EntityMetaIndex.queryTypes')(
    ({
      spaceIds,
      typeDxns,
      inverted = false,
      includeAllQueues = false,
      queues = null,
      window,
    }: {
      spaceIds: readonly EntityMeta['spaceId'][];
      typeDxns: readonly EntityMeta['typeDXN'][];
      inverted?: boolean;
      includeAllQueues?: boolean;
      queues?: readonly QueueRef[] | null;
      window?: QueueWindow;
    }): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sources = sourceRefs(spaceIds, queues);
        if (sources.length === 0 || (typeDxns.length === 0 && !inverted)) {
          return [];
        }

        const sql = this.#sql;
        const limit = yield* SqlBoundVariableLimit;
        const queueWindow = buildQueueWindow(sql, window);
        const budget = limit - countBoundVariables(sql, queueWindow);
        const merge = queueWindowMerge(window);
        const typeCost = measuredVariableCost(sql, (types: readonly string[]) => buildTypeDxnCondition(sql, types));
        const sourceCost = measuredVariableCost(sql, (chunk: readonly SourceRef[]) =>
          buildSourceRefsCondition(sql, chunk, includeAllQueues),
        );
        if (!inverted) {
          // A row matching any type chunk of any source chunk matches the query, so the chunks union.
          const pairs = planChunkPairs(
            { items: typeDxns, costOf: typeCost },
            { items: sources, costOf: sourceCost },
            budget,
          );
          const results = yield* readConsistently(
            sql,
            pairs.length,
            Effect.forEach(
              pairs,
              ([types, chunk]) =>
                sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${buildSourceRefsCondition(sql, chunk, includeAllQueues)} AND ${buildTypeDxnCondition(sql, types)}${queueWindow}`,
            ),
          );
          return mergeChunkedRows(results, merge).map(withDeletedFlag);
        }

        // NOT over a union of type chunks is the intersection of their negations, so the negated
        // type list stays whole in each statement and only the sources are chunked.
        const negatedCost = typeDxns.reduce((sum, typeDXN) => sum + typeCost(typeDXN), 0);
        const widestSource = sources.reduce((widest, source) => Math.max(widest, sourceCost(source)), 0);
        if (negatedCost + widestSource <= budget) {
          const typeWhere = typeDxns.length > 0 ? sql` AND NOT ${buildTypeDxnCondition(sql, typeDxns)}` : sql``;
          const chunks = planChunks(sources, sourceCost, budget - negatedCost);
          const results = yield* readConsistently(
            sql,
            chunks.length,
            Effect.forEach(
              chunks,
              (chunk) =>
                sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${buildSourceRefsCondition(sql, chunk, includeAllQueues)}${typeWhere}${queueWindow}`,
            ),
          );
          return mergeChunkedRows(results, merge).map(withDeletedFlag);
        }

        // Too many types to negate in one statement: find the rows they match, then read the sources
        // without those rows, each statement's limit leaving room for the ones it drops. Always
        // several statements, and a row written between the phases would escape the exclusion.
        const exclusionPairs = planChunkPairs(
          { items: typeDxns, costOf: typeCost },
          { items: sources, costOf: sourceCost },
          limit,
        );
        // Widening the window changes its limit's value, not what it binds, so the second phase plans
        // before the first runs and the cap covers both.
        const sourceChunks = planChunks(sources, sourceCost, budget);
        const statementCount = exclusionPairs.length + sourceChunks.length;
        invariant(statementCount <= MAX_CHUNKED_STATEMENTS, `a read needs ${statementCount} statements`);
        const results = yield* sql.withTransaction(
          Effect.gen(function* () {
            const excluded = new Set<number>();
            for (const [types, chunk] of exclusionPairs) {
              const rows = yield* sql<{
                recordId: number;
              }>`SELECT recordId FROM objectMeta WHERE ${buildSourceRefsCondition(sql, chunk, includeAllQueues)} AND ${buildTypeDxnCondition(sql, types)}`;
              rows.forEach((row) => excluded.add(row.recordId));
            }
            const widenedWindow = buildQueueWindow(sql, widenQueueWindow(window, excluded.size));
            const rows = yield* Effect.forEach(
              sourceChunks,
              (chunk) =>
                sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${buildSourceRefsCondition(sql, chunk, includeAllQueues)}${widenedWindow}`,
            );
            return rows.map((chunkRows) => chunkRows.filter((row) => !excluded.has(row.recordId)));
          }),
        );
        return mergeChunkedRows(results, merge).map(withDeletedFlag);
      }),
  );

  queryRelations = Effect.fn('EntityMetaIndex.queryRelations')(
    ({
      endpoint,
      anchorDxns,
    }: {
      endpoint: 'source' | 'target';
      anchorDxns: readonly string[];
    }): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        if (anchorDxns.length === 0) {
          return [];
        }
        const sql = this.#sql;
        const column = endpoint === 'source' ? 'source' : 'target';
        // A relation carries one value in this column and the anchors are distinct, so chunks
        // partition the matches and the results concatenate without duplicates.
        const results: EntityMeta[] = [];
        for (const chunk of chunkArray([...new Set(anchorDxns)])) {
          const rows =
            yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE entityKind = 'relation' AND ${sql.in(column, chunk)}`;
          results.push(...rows);
        }
        return results.map((row) => ({
          ...row,
          deleted: !!row.deleted,
        }));
      }),
  );

  // TODO(dmaretskyi): Update recordId on objects so that we don't need to look it up separately.
  update = Effect.fn('EntityMetaIndex.update')((objects: IndexerObject[]): Effect.Effect<void, SqlError.SqlError> =>
    Effect.gen({ self: this }, function* () {
      const sql = this.#sql;

      yield* Effect.forEach(
        objects,
        (object) =>
          Effect.gen({ self: this }, function* () {
            const { spaceId, queueId, queueNamespace, documentId, data, queuePosition } = object;

            // Extract metadata (Logic emulating Echo APIs as strict imports are unavailable).
            const castData = data;
            const objectId = castData.id;

            // Check for existing record by (spaceId, queueId) or (spaceId, documentId).
            type ExistingRow = {
              recordId: number;
              entityKind: string;
              typeDXN: string;
              source: string | null;
              target: string | null;
              parent: string | null;
              parentId: string | null;
              sourceId: string | null;
              targetId: string | null;
              convergenceKey: string | null;
              annotations: string | null;
            };
            let existing: readonly ExistingRow[];
            if (documentId) {
              existing =
                yield* sql<ExistingRow>`SELECT recordId, entityKind, typeDXN, source, target, parent, parentId, sourceId, targetId, convergenceKey, annotations FROM objectMeta WHERE spaceId = ${spaceId} AND documentId = ${documentId} AND objectId = ${objectId} LIMIT 1`;
            } else if (queueId) {
              existing =
                yield* sql<ExistingRow>`SELECT recordId, entityKind, typeDXN, source, target, parent, parentId, sourceId, targetId, convergenceKey, annotations FROM objectMeta WHERE spaceId = ${spaceId} AND queueId = ${queueId} AND objectId = ${objectId} LIMIT 1`;
            } else {
              // Should not happen based on IndexerObject definition (one must be present ideally), but handle gracefully.
              existing = [];
            }

            // Get max version + 1.
            const result = yield* sql<{ v: number | null }>`SELECT MAX(version) as v FROM objectMeta`;
            const [{ v }] = result;
            const version = (v ?? 0) + 1;

            // A partial block carries no `@type`/body — notably the `{ id, '@deleted': true }`
            // tombstone appended by `Feed.remove`. Preserve the prior row's body-derived columns
            // (type, kind, relation endpoints, parent) rather than recomputing them from the empty
            // block; otherwise `typeDXN` collapses to the `'type'` fallback and type-scoped queries
            // with `deleted: 'include'` stop matching the deleted object. Only `deleted`/`version`/
            // `updatedAt` advance for such a block.
            const priorRow = existing.length > 0 ? existing[0] : undefined;
            const isPartialBlock = castData[ATTR_TYPE] === undefined;
            const preserveBody = isPartialBlock && priorRow !== undefined;

            // Extract metadata.
            const entityKind = preserveBody
              ? priorRow.entityKind
              : castData[ATTR_RELATION_SOURCE]
                ? 'relation'
                : 'object';
            // Type identifier as stored on `system.type`: a typename DXN for static schemas,
            // an `echo:` EID for stored (dynamic) schemas. Normalize the EID form so the indexed
            // value matches the normalized value the query path compares against (legacy
            // single-slash `echo:/<id>` and canonical `echo:///<id>` address the same schema).
            // A preserved prior row was normalized when it was written, so it needs no re-normalizing.
            const typeDXN = preserveBody
              ? priorRow.typeDXN
              : URI.make(_normalizeTypeUri(castData[ATTR_TYPE] ? String(castData[ATTR_TYPE]) : 'type'));
            const deleted = castData[ATTR_DELETED] ? 1 : 0;
            // Relations.
            const source = preserveBody
              ? priorRow.source
              : entityKind === 'relation'
                ? (castData[ATTR_RELATION_SOURCE] ?? null)
                : null;
            const target = preserveBody
              ? priorRow.target
              : entityKind === 'relation'
                ? (castData[ATTR_RELATION_TARGET] ?? null)
                : null;
            // Parent (nullable).
            const parent = preserveBody ? priorRow.parent : (castData[ATTR_PARENT] ?? null);
            // Bare local ids of the dependencies, the columns the query compiler joins through.
            const parentId = preserveBody ? priorRow.parentId : localEntityId(parent, spaceId);
            const sourceId = preserveBody ? priorRow.sourceId : localEntityId(source, spaceId);
            const targetId = preserveBody ? priorRow.targetId : localEntityId(target, spaceId);
            // Convergence key (nullable) — from the meta section of the serialized object. The meta
            // arrives as raw replicated JSON, so anything but a string is treated as no key.
            const rawConvergenceKey = (castData[ATTR_META] as { convergenceKey?: unknown } | undefined)?.convergenceKey;
            const convergenceKey = preserveBody
              ? priorRow.convergenceKey
              : typeof rawConvergenceKey === 'string'
                ? rawConvergenceKey
                : null;

            const rawAnnotations = (castData[ATTR_META] as { annotations?: unknown } | undefined)?.annotations;
            const annotations = preserveBody
              ? priorRow.annotations
              : rawAnnotations !== null && typeof rawAnnotations === 'object' && Object.keys(rawAnnotations).length > 0
                ? JSON.stringify(rawAnnotations)
                : null;

            const updatedAtTimestamp = object.updatedAt;
            // Prefer the creation timestamp stored in the document (survives compaction/migrations).
            // Fall back to the automerge-derived updatedAt for legacy objects that predate this field.
            const createdAtTimestamp = object.createdAt ?? updatedAtTimestamp;

            if (existing.length > 0) {
              // Feed entries collapse by id to the latest whole-object block — a re-append reusing
              // an existing id (a live feed object's `Obj.update`) UPDATEs this row wholesale.
              // TODO(wittjosiah): With partial-update blocks (see `EchoFeedCodec.encode`'s TODO),
              // this becomes a field-level last-write-wins merge instead of a wholesale replace.
              yield* sql`
                  UPDATE objectMeta SET
                    version = ${version},
                    queueNamespace = ${queueNamespace ?? ''},
                    entityKind = ${entityKind},
                    typeDXN = ${typeDXN},
                    deleted = ${deleted},
                    source = ${source},
                    target = ${target},
                    parent = ${parent},
                    parentId = ${parentId},
                    sourceId = ${sourceId},
                    targetId = ${targetId},
                    convergenceKey = ${convergenceKey},
                    annotations = ${annotations},
                    updatedAt = ${updatedAtTimestamp},
                    queuePosition = ${queuePosition ?? null}
                  WHERE recordId = ${existing[0].recordId}
                `;
            } else {
              yield* sql`
                  INSERT INTO objectMeta (
                    objectId, queueId, queueNamespace, spaceId, documentId,
                    entityKind, typeDXN, deleted, source, target, parent, parentId, sourceId, targetId,
                    convergenceKey, annotations, version, createdAt, updatedAt, queuePosition
                  ) VALUES (
                    ${objectId}, ${queueId ?? ''}, ${queueNamespace ?? ''}, ${spaceId}, ${documentId ?? ''},
                    ${entityKind}, ${typeDXN}, ${deleted},
                    ${source}, ${target}, ${parent}, ${parentId}, ${sourceId}, ${targetId},
                    ${convergenceKey}, ${annotations}, ${version},
                    ${createdAtTimestamp}, ${updatedAtTimestamp}, ${queuePosition ?? null}
                  )
                `;
            }
          }),
        { discard: true },
      );
    }),
  );

  /**
   * Look up `recordIds` for objects that are already stored in the EntityMetaIndex.
   * Mutates the objects in place.
   */
  lookupRecordIds = Effect.fn('EntityMetaIndex.lookupRecordIds')(
    (objects: IndexerObject[]): Effect.Effect<void, SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;

        for (const object of objects) {
          const { spaceId, queueId, documentId, data } = object;
          const objectId = data.id;

          let result: readonly { recordId: number }[];
          if (documentId) {
            result = yield* sql<{
              recordId: number;
            }>`SELECT recordId FROM objectMeta WHERE spaceId = ${spaceId} AND documentId = ${documentId} AND objectId = ${objectId} LIMIT 1`;
          } else if (queueId) {
            result = yield* sql<{
              recordId: number;
            }>`SELECT recordId FROM objectMeta WHERE spaceId = ${spaceId} AND queueId = ${queueId} AND objectId = ${objectId} LIMIT 1`;
          } else {
            result = [];
          }

          if (result.length === 0) {
            // TODO(mykola): Handle this case gracefully.
            return yield* Effect.die(
              new Error(`Object not found in EntityMetaIndex: ${spaceId}/${documentId ?? queueId}/${objectId}`),
            );
          }
          object.recordId = result[0].recordId;
        }
      }),
  );

  /**
   * Look up object metadata by recordIds.
   */
  lookupByRecordIds = Effect.fn('EntityMetaIndex.lookupByRecordIds')(
    (recordIds: number[]): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        // Chunked: a widely-referenced object can put thousands of ids here, past
        // SQLITE_LIMIT_VARIABLE_NUMBER.
        const results: EntityMeta[] = [];
        for (const chunk of chunkArray(recordIds)) {
          const rows = yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${sql.in('recordId', chunk)}`;
          for (const row of rows) {
            results.push({ ...row, deleted: !!row.deleted });
          }
        }
        return results;
      }),
  );

  /**
   * Record ids of rows belonging to whole documents or specific objects — the set garbage
   * collection reclaims across the dependent indexes.
   */
  selectRecordIdsForRemoval = Effect.fn('EntityMetaIndex.selectRecordIdsForRemoval')(
    (query: {
      spaceId: SpaceId;
      documentIds: readonly string[];
      objects: readonly { documentId: string; objectId: string }[];
    }): Effect.Effect<number[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        const recordIds = new Set<number>();

        for (const chunk of chunkArray(query.documentIds)) {
          const rows = yield* sql<{
            recordId: number;
          }>`SELECT recordId FROM objectMeta WHERE spaceId = ${query.spaceId} AND ${sql.in('documentId', chunk)}`;
          rows.forEach((row) => recordIds.add(row.recordId));
        }

        for (const chunk of chunkArray(query.objects, chunkSizeForBoundVariables(2))) {
          const conditions = chunk.map(
            (object) => sql`(documentId = ${object.documentId} AND objectId = ${object.objectId})`,
          );
          const rows = yield* sql<{
            recordId: number;
          }>`SELECT recordId FROM objectMeta WHERE spaceId = ${query.spaceId} AND (${sql.or(conditions)})`;
          rows.forEach((row) => recordIds.add(row.recordId));
        }

        return [...recordIds];
      }),
  );

  /** Delete metadata rows by record id. */
  deleteByRecordIds = Effect.fn('EntityMetaIndex.deleteByRecordIds')(
    (recordIds: readonly number[]): Effect.Effect<void, SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        for (const chunk of chunkArray(recordIds)) {
          yield* sql`DELETE FROM objectMeta WHERE ${sql.in('recordId', chunk)}`;
        }
      }),
  );

  /**
   * Look up object metadata by object id across one or more spaces (space db and queue items).
   */
  queryObjectIds = Effect.fn('EntityMetaIndex.queryObjectIds')(
    (query: {
      spaceIds: readonly EntityMeta['spaceId'][];
      objectIds: readonly EntityMeta['objectId'][];
    }): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        if (query.spaceIds.length === 0 || query.objectIds.length === 0) {
          return [];
        }

        const sql = this.#sql;
        // Both lists bind one variable per element, so each chunk takes half the budget; the ids
        // are distinct, so a row matches exactly one pair of chunks and the results concatenate
        // without duplicates.
        const chunkSize = chunkSizeForBoundVariables(2);
        const results: EntityMeta[] = [];
        for (const spaceIdChunk of chunkArray([...new Set(query.spaceIds)], chunkSize)) {
          for (const objectIdChunk of chunkArray([...new Set(query.objectIds)], chunkSize)) {
            const rows =
              yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${sql.in('spaceId', spaceIdChunk)} AND ${sql.in('objectId', objectIdChunk)}`;
            for (const row of rows) {
              results.push({ ...row, deleted: !!row.deleted });
            }
          }
        }
        return results;
      }),
  );

  /**
   * Look up object metadata by objectId, spaceId, and queueId.
   */
  lookupByObjectId = Effect.fn('EntityMetaIndex.lookupByObjectId')(
    (query: {
      objectId: string;
      spaceId: string;
      queueId: string;
    }): Effect.Effect<EntityMeta | null, SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        const rows =
          yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE spaceId = ${query.spaceId} AND queueId = ${query.queueId} AND objectId = ${query.objectId} LIMIT 1`;

        if (rows.length === 0) {
          return null;
        }

        return {
          ...rows[0],
          deleted: !!rows[0].deleted,
        };
      }),
  );

  /**
   * Query objects by timestamp range.
   */
  queryByTimeRange = Effect.fn('EntityMetaIndex.queryByTimeRange')(
    (query: {
      spaceIds: readonly string[];
      updatedAfter?: number;
      updatedBefore?: number;
      createdAfter?: number;
      createdBefore?: number;
      includeAllQueues?: boolean;
      queues?: readonly QueueRef[] | null;
    }): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sources = sourceRefs(query.spaceIds, query.queues);
        if (sources.length === 0) {
          return [];
        }

        const sql = this.#sql;
        const includeAllQueues = query.includeAllQueues ?? false;
        const timeConditions: Statement.Fragment[] = [];
        if (query.updatedAfter != null) {
          timeConditions.push(sql`updatedAt >= ${query.updatedAfter}`);
        }
        if (query.updatedBefore != null) {
          timeConditions.push(sql`updatedAt <= ${query.updatedBefore}`);
        }
        if (query.createdAfter != null) {
          timeConditions.push(sql`createdAt >= ${query.createdAfter}`);
        }
        if (query.createdBefore != null) {
          timeConditions.push(sql`createdAt <= ${query.createdBefore}`);
        }

        const timeFilter = timeConditions.length > 0 ? sql` AND ${sql.and(timeConditions)}` : sql``;
        const budget = (yield* SqlBoundVariableLimit) - countBoundVariables(sql, timeFilter);
        const sourceCost = measuredVariableCost(sql, (chunk: readonly SourceRef[]) =>
          buildSourceRefsCondition(sql, chunk, includeAllQueues),
        );
        const chunks = planChunks(sources, sourceCost, budget);
        const results = yield* readConsistently(
          sql,
          chunks.length,
          Effect.forEach(
            chunks,
            (chunk) =>
              sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${buildSourceRefsCondition(sql, chunk, includeAllQueues)}${timeFilter}`,
          ),
        );
        return mergeChunkedRows(results).map(withDeletedFlag);
      }),
  );

  /**
   * Query children by parent object ids.
   * Matches both:
   * - Objects whose `parent` field references one of the given parent ids (standard parent/child hierarchy).
   * - Queue items whose `queueId` equals one of the parent ids (e.g. items inside a Feed, since a feed's queue
   *   DXN uses the feed's object id as its queue id — see `Feed.getFeedUri`).
   */
  queryChildren = Effect.fn('EntityMetaIndex.queryChildren')(
    (query: { spaceId: SpaceId[]; parentIds: EntityId[] }): Effect.Effect<readonly EntityMeta[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        if (query.parentIds.length === 0) {
          return [];
        }

        const sql = this.#sql;
        // Each parent id binds twice (as a DXN and as a queue id) and each space id once, so a
        // third of the budget per list keeps the statement whole.
        const chunkSize = chunkSizeForBoundVariables(3);
        // A row can match one chunk by `parent` and another by `queueId`, so it is deduplicated.
        const byRecordId = new Map<number, EntityMeta>();
        for (const spaceIds of chunkArray(query.spaceId, chunkSize)) {
          for (const parentIds of chunkArray(query.parentIds, chunkSize)) {
            const parentDxns = parentIds.map((id) => EID.make({ entityId: id }));
            const rows =
              yield* sql<EntityMeta>`SELECT * FROM objectMeta WHERE ${sql.in('spaceId', spaceIds)} AND (${sql.in('parent', parentDxns)} OR ${sql.in('queueId', parentIds)})`;
            for (const row of rows) {
              byRecordId.set(row.recordId, { ...row, deleted: !!row.deleted });
            }
          }
        }

        return [...byRecordId.values()];
      }),
  );
}
